import React, { useEffect, useState } from 'react';
import { tintFor } from '../../utils/tints';
import storyService from '../../services/storyService';
import ringArt from '../../assets/img/portal-ring.png';

/**
 * The host's own Portal in the viewer (story-activity handoff §3): where a guest gets the action
 * row, the host gets a handle — an up-chevron nudging, and a pill of the first faces with
 * "{n} DOWN · {n} VIEWS" — and a swipe up (or a tap) opens the activity sheet: WHO'S DOWN and
 * VIEWS, newest first, the viewers who are also in tagged DOWN. The story is paused while it's open.
 */
const TINTS = ['#A8D8EA', '#FAE1B4', '#FFCCE6', '#C6B8FF', '#22ECFB', '#FCAA6D'];
const SWIPE_PX = 40;

/** `JUST NOW` · `2M AGO` · `3H AGO` · `2D AGO`. */
export function agoCaps(iso, now = Date.now()) {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return '';
  const mins = Math.floor((now - t) / 60000);
  if (mins < 1) return 'JUST NOW';
  if (mins < 60) return `${mins}M AGO`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}H AGO`;
  return `${Math.floor(hours / 24)}D AGO`;
}

function Face({ person, size, index = 0, ring = false }) {
  const tint = TINTS[index % TINTS.length] || tintFor(person?.userId);
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, flexShrink: 0, borderRadius: '50%', background: tint, color: '#000', border: ring ? '2px solid #000' : 0, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size > 30 ? 14 : 11, fontWeight: 700 }}
    >
      {person?.avatarUrl ? <img src={person.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : (person?.name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

/** A vertical swipe: `dir` −1 for up, +1 for down; `fn` fires past 40px. */
export const swipeFrom = (dir, fn) => (e) => {
  const y0 = e.clientY;
  const up = (ev) => {
    window.removeEventListener('pointerup', up);
    if (dir * (ev.clientY - y0) > SWIPE_PX) fn();
  };
  window.addEventListener('pointerup', up);
};

/** The handle at the bottom of the host's card. */
export function PortalActivityHandle({ down = [], downCount = 0, waitingCount = 0, viewCount = 0, onOpen, reduced = false }) {
  return (
    <>
      <div aria-hidden data-testid="activity-swipe" onPointerDown={swipeFrom(-1, onOpen)} style={{ position: 'absolute', left: 0, right: 0, top: 90, bottom: 0, zIndex: 2, touchAction: 'none', cursor: 'ns-resize' }} />
      <button
        type="button"
        onPointerDown={swipeFrom(-1, onOpen)}
        onClick={onOpen}
        aria-label="Swipe up to see who's down and who viewed"
        data-testid="activity-handle"
        className="font-mono"
        style={{ position: 'absolute', left: '50%', bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))', zIndex: 5, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'transparent', color: '#fff', border: 0, fontFamily: 'inherit', cursor: 'pointer', touchAction: 'none' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter" aria-hidden style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.9))', animation: reduced ? 'none' : 'prcNudge 1.6s ease-in-out infinite' }}><path d="M5 15l7-7 7 7" /></svg>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px 0 4px', background: 'rgba(0,0,0,.72)', borderRadius: 999 }}>
          <span style={{ display: 'flex' }}>
            {down.slice(0, 3).map((p, i) => <span key={p.userId || i} style={{ marginRight: -8 }}><Face person={p} size={28} index={i} ring /></span>)}
          </span>
          <span style={{ marginLeft: down.length ? 8 : 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>{waitingCount > 0 ? `${waitingCount} TO LET IN` : `${downCount} DOWN`} · {viewCount} VIEWS</span>
        </span>
      </button>
    </>
  );
}

const tabStyle = (on) => ({ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: on ? '#fff' : 'rgba(255,255,255,.12)', color: on ? '#000' : '#fff', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer', transition: 'background-color .2s, color .2s' });
const badgeStyle = (on) => ({ minWidth: 22, height: 20, padding: '0 6px', boxSizing: 'border-box', borderRadius: 999, background: on ? 'rgba(0,0,0,.1)' : 'rgba(255,255,255,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 });

/**
 * The sheet: WHO'S DOWN · VIEWS. `activity` = { down, views }. On a curated Portal the waiting
 * rows carry ✕ (pass) and LET IN, and the admitted ones an IN tag (curated-portal handoff §3).
 */
export function PortalActivitySheet({ activity, mode = 'open', onLetIn = null, onPass = null, onClose, onShare = null, onManage = null, reduced = false }) {
  const [tab, setTab] = useState('down');
  const down = activity?.down || [];
  const views = activity?.views || [];
  const rows = tab === 'down' ? down : views;
  return (
    <>
      <div aria-hidden data-testid="activity-scrim" onPointerDown={onClose} style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(0,0,0,.45)', animation: reduced ? 'none' : 'prcFadeIn .2s linear both' }} />
      <div role="dialog" aria-label="Activity" data-testid="activity-sheet" className="font-mono text-white" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 21, height: '68%', background: '#0B0B0B', padding: '10px 16px 16px', boxSizing: 'border-box', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gridTemplateRows: onShare || onManage ? 'max-content max-content minmax(0,1fr) max-content' : 'max-content max-content minmax(0,1fr)', gap: 12, animation: reduced ? 'none' : 'prcSlideUp .44s cubic-bezier(.2,.7,.2,1) both' }}>
        <span aria-hidden data-testid="activity-grab" onPointerDown={swipeFrom(1, onClose)} style={{ justifySelf: 'center', width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.3)', cursor: 'ns-resize', padding: '6px 20px', backgroundClip: 'content-box', touchAction: 'none' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
          <button type="button" aria-pressed={tab === 'down'} onClick={() => setTab('down')} style={tabStyle(tab === 'down')}>WHO’S DOWN<span style={badgeStyle(tab === 'down')}>{down.length}</span></button>
          <button type="button" aria-pressed={tab === 'views'} onClick={() => setTab('views')} style={tabStyle(tab === 'views')}>VIEWS<span style={badgeStyle(tab === 'views')}>{views.length}</span></button>
        </div>
        <div className="prc-row" style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, margin: '0 -4px', padding: '0 4px' }} data-testid="activity-rows">
          {!activity && <span style={{ padding: '10px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.55)' }}>LOADING…</span>}
          {activity && rows.length === 0 && (
            <span style={{ padding: '10px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.55)' }}>{tab === 'down' ? 'NOBODY’S DOWN YET.' : 'NO VIEWS YET.'}</span>
          )}
          {rows.map((p, i) => (
            <div key={p.userId || i} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '6px 14px 6px 6px', background: 'rgba(255,255,255,.07)', borderRadius: 999 }}>
              <Face person={p} size={40} index={i} />
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,.55)' }}>
                  {tab === 'views' ? 'VIEWED' : mode === 'curated' ? (p.status === 'pending' ? 'ASKED' : 'IN') : 'DOWN'} · {agoCaps(p.at)}
                </span>
              </span>
              {tab === 'views' && p.isDown && <span style={{ flexShrink: 0, height: 24, padding: '0 10px', display: 'flex', alignItems: 'center', background: '#00FFFF', color: '#000', borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em' }}>DOWN</span>}
              {tab === 'down' && mode === 'curated' && p.status !== 'pending' && <span data-testid="in-tag" style={{ flexShrink: 0, height: 24, padding: '0 10px', display: 'flex', alignItems: 'center', background: '#00FFFF', color: '#000', borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em' }}>IN</span>}
              {tab === 'down' && mode === 'curated' && p.status === 'pending' && (
                <>
                  <button type="button" aria-label={`Pass on ${p.name}`} onClick={() => onPass?.(p.userId)} className="prc-pill" style={{ flexShrink: 0, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(255,255,255,.12)', color: '#fff', border: 0, borderRadius: '50%', cursor: 'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                  <button type="button" onClick={() => onLetIn?.(p.userId)} style={{ flexShrink: 0, height: 32, padding: '0 14px', background: '#fff', color: '#000', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer' }}>LET IN</button>
                </>
              )}
            </div>
          ))}
        </div>
        {(onShare || onManage) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6 }} data-testid="activity-footer">
            <button type="button" onClick={onShare} className="prc-pill" style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(255,255,255,.12)', color: '#fff', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer' }}>
              <ShareGlyph size={13} />SHARE
            </button>
            <button type="button" onClick={onManage} style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#fff', color: '#000', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer' }}>
              MANAGE PORTAL<ArrowGlyph />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export const ShareGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M12 3v13M7 8l5-5 5 5" /><path d="M5 13v8h14v-8" /></svg>
);
export const ArrowGlyph = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

/** SHARE above MANAGE at the bottom-right of the host's own card (share/city/manage handoff §B). */
export function HostChrome({ onShare, onManage }) {
  const label = { fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textShadow: '0 1px 3px rgba(0,0,0,.9)' };
  const btn = { position: 'absolute', right: 12, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 0, background: 'transparent', color: '#fff', border: 0, fontFamily: 'inherit', cursor: 'pointer' };
  return (
    <div className="font-mono" data-testid="host-chrome">
      <button type="button" onClick={onShare} aria-label="Share your Portal" style={{ ...btn, bottom: 'calc(92px + env(safe-area-inset-bottom, 0px))' }}>
        <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShareGlyph /></span>
        <span style={label}>SHARE</span>
      </button>
      <button type="button" onClick={onManage} aria-label="Manage your Portal" style={{ ...btn, bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}>
        <span style={{ position: 'relative', width: 44, height: 44, display: 'block' }}>
          <span aria-hidden style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: 'prcSpin 6s linear infinite' }} />
          <span style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ArrowGlyph /></span>
        </span>
        <span style={label}>MANAGE</span>
      </button>
    </div>
  );
}

/** Fetches the host's activity for a Portal; `refresh` re-fetches (on open). */
export function usePortalActivity(portalId, enabled) {
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    if (!portalId || !enabled) return undefined;
    let done = false;
    storyService.getActivity(portalId).then((r) => { if (!done && r?.success) setActivity(r.data || { down: [], views: [] }); });
    return () => { done = true; };
  }, [portalId, enabled]);
  return activity;
}
